import { useState, memo, useMemo, useCallback } from 'react';
import { ChevronDown, ChevronUp, FileText, BookOpen, Scale, FileCheck, CheckCircle2, FileEdit, HelpCircle, Image as ImageIcon, LucideIcon } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { useSessionStore } from '@/stores';
import { useNotificationStore } from '@/stores';
import { draftApi } from '@/services/api';
import { API_CONFIG } from '@/config/api.config';

interface MessageContextResource {
  resource_type: string;
  resource_id: string;
  resource_name?: string;
  use_summary?: boolean;
  use_biblio_info?: boolean;
  use_abstract?: boolean;
  use_entire_description?: boolean;
  use_claims?: boolean;
  paragraphs?: number[];
  claims?: number[];
}

interface VDBResource {
  mpep_sections?: string[];
  cfr_sections?: string[];
  usc_sections?: string[];
  cfr_info?: Array<{
    section: string;
    title: string;
    content?: string;
  }>;
  fed_reg_docs?: Array<{
    document_number: string;
    title: string;
  }>;
  classification_codes?: string[];
}

/**
 * One drawing sheet an answer was given.
 *
 * A model's reading of a drawing is the one thing in a chat nobody should have to take
 * on trust, so the sheet it read is offered back: the answer says "FIG. 3, page 4" and
 * this is what opens it.
 */
interface MessageFigure {
  resource_id: string;
  label?: string;
  figure: string;
  page: number;
}

interface MessageContextProps {
  messageContext?: {
    resources?: MessageContextResource[];
    figures?: MessageFigure[];
    vdb_discovered_resources?: VDBResource;
    search_terms?: string[];
    mpep_sections?: string[];
    cfr_sections?: string[];
    usc_sections?: string[];
  };
  sessionContext?: any;
  onDocumentClick?: (doc: any) => void;
  compactMode?: boolean;
}

interface ResourceBadgeProps {
  resource: MessageContextResource;
  expanded: boolean;
  getResourceIcon: (type: string | undefined, resourceName?: string) => LucideIcon;
  getResourceDisplayName: (resource: MessageContextResource) => string;
  isResourceInContext: (resource: MessageContextResource) => boolean;
  handleAddToContext: (resource: MessageContextResource) => Promise<void>;
  onDocumentClick?: (doc: any) => void;
}

// Move ResourceBadge outside the main component to prevent re-renders
const ResourceBadge = memo(({
  resource,
  expanded,
  getResourceIcon,
  getResourceDisplayName,
  isResourceInContext,
  handleAddToContext,
  onDocumentClick
}: ResourceBadgeProps) => {
  const Icon = getResourceIcon(resource.resource_type, resource.resource_name);
  const inContext = isResourceInContext(resource);

  const handleResourceClick = () => {
    // Check if this is a draft - either by resource_type or by resource_name pattern
    const isDraft = (resource.resource_type && resource.resource_type.toLowerCase() === 'draft') ||
                    (resource.resource_name && (
                      resource.resource_name.includes('Active Drafts:') ||
                      resource.resource_name.includes('Draft Content')
                    ));

    // Handle draft clicks differently - open in split view
    if (isDraft) {
      localStorage.setItem('open_draft_id', resource.resource_id);
      window.dispatchEvent(new Event('open-draft'));
    } else if (onDocumentClick && resource.resource_type) {
      // For all other document types (patents, publications, applications, uploads, etc.)
      // Open them in the document viewer
      const displayName = getResourceDisplayName(resource);
      const docType = resource.resource_type.charAt(0).toUpperCase() + resource.resource_type.slice(1);

      // Structure the document object based on type
      if (resource.resource_type === 'upload') {
        onDocumentClick({
          id: `upload-${resource.resource_id}`,
          title: displayName,
          type: 'Upload',
          status: 'Active',
          resource_id: resource.resource_id,
          file_type: 'upload',
          file_name: displayName
        });
      } else {
        onDocumentClick({
          id: resource.resource_id,
          title: resource.resource_id,
          type: docType,
          status: 'Active',
          resource_id: resource.resource_id,
        });
      }
    }
  };

  if (!expanded) {
    // Collapsed mode: compact inline display with click
    return (
      <span
        onClick={(e) => {
          e.stopPropagation();
          handleResourceClick();
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '0.25rem',
          background: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '0.25rem',
          padding: '0.125rem 0.5rem',
          fontSize: '0.75rem',
          cursor: 'pointer',
          pointerEvents: 'auto'
        }}
        title={`Click to view ${getResourceDisplayName(resource)}`}
      >
        <Icon className="w-3 h-3 text-gray-600 flex-shrink-0 pointer-events-none" />
        <span className="font-medium text-gray-900 pointer-events-none">
          {getResourceDisplayName(resource)}
        </span>
        {inContext && (
          <CheckCircle2 className="w-3 h-3 text-green-600 flex-shrink-0 pointer-events-none" />
        )}
      </span>
    );
  }

  // Expanded mode: full card with details and click
  return (
    <div
      onClick={(e) => {
        e.stopPropagation();
        handleResourceClick();
      }}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        background: 'white',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        padding: '0.5rem',
        cursor: 'pointer',
        pointerEvents: 'auto'
      }}
      title={`Click to view ${getResourceDisplayName(resource)}`}
    >
      <div className="flex items-center gap-2 flex-1 min-w-0 pointer-events-none">
        <Icon className="w-4 h-4 text-gray-600 flex-shrink-0" />
        <span className="text-sm font-medium text-gray-900 truncate">
          {getResourceDisplayName(resource)}
        </span>
      </div>

      <div className="flex items-center gap-1 flex-shrink-0 pointer-events-none">
        {resource.paragraphs && resource.paragraphs.length > 0 && (
          <Badge variant="secondary" className="text-xs">
            ¶{resource.paragraphs.join(', ')}
          </Badge>
        )}
        {resource.claims && resource.claims.length > 0 && (
          <Badge variant="secondary" className="text-xs">
            Cl. {resource.claims.join(', ')}
          </Badge>
        )}
        {resource.use_summary && (
          <Badge variant="secondary" className="text-xs">Summary</Badge>
        )}
        {resource.use_abstract && (
          <Badge variant="secondary" className="text-xs">Abstract</Badge>
        )}
        {resource.use_claims && (
          <Badge variant="secondary" className="text-xs">Claims</Badge>
        )}
        {resource.use_entire_description && (
          <Badge variant="secondary" className="text-xs">Full Desc</Badge>
        )}
      </div>

      {inContext ? (
        <div className="flex items-center gap-1 text-xs text-green-600 flex-shrink-0">
          <CheckCircle2 className="w-4 h-4" />
          <span>In Session</span>
        </div>
      ) : (
        <Button
          size="sm"
          variant="ghost"
          className="h-6 px-2 text-xs flex-shrink-0"
          onClick={(e) => {
            e.stopPropagation();
            handleAddToContext(resource);
          }}
        >
          Add to Session
        </Button>
      )}
    </div>
  );
});

/** Where one drawing sheet is served from, for the viewer and for a new tab. */
function figurePageUrl(figure: MessageFigure): string {
  const token = localStorage.getItem('id_token') ?? '';
  const query = new URLSearchParams({
    resource_id: figure.resource_id,
    page: String(figure.page),
    id_token: token,
  });
  return `${API_CONFIG.baseUrl}/chat/figure_page?${query.toString()}`;
}

const MessageContextComponent = ({ messageContext, sessionContext, onDocumentClick, compactMode = false }: MessageContextProps) => {
  // All hooks must be at the top of the component, before any conditional returns
  const [expanded, setExpanded] = useState(false);
  const [showHelpModal, setShowHelpModal] = useState(false);
  // The sheet being looked at, if any. Opened in place rather than in a new tab: the
  // attorney is checking the answer against the drawing and wants both on screen.
  const [viewing, setViewing] = useState<MessageFigure | null>(null);
  const { addNotification } = useNotificationStore();

  // Get icon for resource type - memoized to prevent re-renders
  const getResourceIcon = useCallback((type: string | undefined, resourceName?: string): LucideIcon => {
    // Check if it's a draft by name pattern
    if (resourceName && (resourceName.includes('Active Drafts:') || resourceName.includes('Draft Content'))) {
      return FileEdit;
    }

    if (!type) return FileText;

    switch (type) {
      case 'patent':
      case 'publication':
      case 'application':
        return FileText;
      case 'upload':
        return FileCheck;
      case 'draft':
        return FileEdit;
      case 'pros_doc':
      case 'prosecution_document':
        return FileText;
      default:
        return FileText;
    }
  }, []);

  // Format resource display name
  const getResourceDisplayName = useCallback((resource: MessageContextResource) => {
    // Special handling for drafts - look up the actual name from active_drafts
    if (resource.resource_type === 'draft') {
      try {
        const activeDraftsStr = localStorage.getItem('active_drafts');
        const activeDrafts = activeDraftsStr ? JSON.parse(activeDraftsStr) : [];
        const draft = activeDrafts.find((d: any) => d.draft_id === resource.resource_id);

        if (draft && draft.title) {
          return draft.title;
        }
      } catch (error) {
        console.error('Error looking up draft name:', error);
      }
    }

    // Special handling for uploads - look up the actual name from session_context
    if (resource.resource_type === 'upload') {
      try {
        const storedContext = localStorage.getItem('session_context');
        if (storedContext && storedContext !== 'undefined') {
          const context = JSON.parse(storedContext);
          const resourceIds = context.resource_ids || [];
          const resourceNames = context.resource_names || [];

          // Find the index of this upload's ID in resource_ids
          const index = resourceIds.indexOf(resource.resource_id);
          if (index !== -1 && resourceNames[index]) {
            return resourceNames[index];
          }
        }
      } catch (error) {
        console.error('Error looking up upload name:', error);
      }
    }

    // Special handling for prosecution documents - look up the actual name from session_context
    if (resource.resource_type === 'pros_doc' || resource.resource_type === 'prosecution_document') {
      try {
        const storedContext = localStorage.getItem('session_context');
        if (storedContext && storedContext !== 'undefined') {
          const context = JSON.parse(storedContext);
          const prosDocIds = context.pros_docs || [];
          const prosDocNames = context.pros_doc_names || [];

          // Find the index of this prosecution document's ID in pros_docs
          const index = prosDocIds.indexOf(resource.resource_id);
          if (index !== -1 && prosDocNames[index]) {
            return prosDocNames[index];
          }
        }
      } catch (error) {
        console.error('Error looking up prosecution document name:', error);
      }
    }

    if (resource.resource_name) {
      // Clean up draft name by removing "Active Drafts:" prefix
      if (resource.resource_name.includes('Active Drafts:')) {
        return resource.resource_name.replace(/^Active Drafts:\s*/, '').trim();
      }
      return resource.resource_name;
    }
    if (!resource.resource_type) {
      return resource.resource_id || 'Unknown Resource';
    }
    // Convert resource_type to title case and replace underscores with spaces
    // e.g., "foreign_patent" -> "Foreign Patent", "pros_doc" -> "Pros Doc"
    const type = resource.resource_type
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
    return `${type} ${resource.resource_id}`;
  }, []);

  // Check if resource is already in context
  const isResourceInContext = useCallback((resource: MessageContextResource) => {
    if (!resource.resource_type) return false;

    const storedContext = localStorage.getItem('session_context');
    if (!storedContext || storedContext === 'undefined') return false;

    try {
      const context = JSON.parse(storedContext);

      // Check based on resource type
      if (resource.resource_type === 'upload') {
        const resourceIds = context.resource_ids || [];
        return resourceIds.includes(resource.resource_id);
      } else if (resource.resource_type === 'patent') {
        const patents = context.current_patents || [];
        return patents.includes(resource.resource_id);
      } else if (resource.resource_type === 'publication') {
        const publications = context.current_publications || [];
        return publications.includes(resource.resource_id);
      } else if (resource.resource_type === 'application') {
        const applications = context.current_applications || [];
        return applications.includes(resource.resource_id);
      } else if (resource.resource_type === 'draft') {
        // Check active drafts from localStorage
        const activeDraftsStr = localStorage.getItem('active_drafts');
        const activeDrafts = activeDraftsStr ? JSON.parse(activeDraftsStr) : [];
        return activeDrafts.some((draft: any) => draft.draft_id === resource.resource_id);
      } else if (resource.resource_type === 'pros_doc' || resource.resource_type === 'prosecution_document') {
        const prosDocs = context.pros_docs || [];
        return prosDocs.includes(resource.resource_id);
      }

      return false;
    } catch (error) {
      return false;
    }
  }, []);

  // Add resource to context
  const handleAddToContext = useCallback(async (resource: MessageContextResource) => {
    if (!resource.resource_type) {
      addNotification({
        type: 'error',
        title: 'Invalid Resource',
        message: 'Resource type is missing',
      });
      return;
    }

    try {
      const storedContext = localStorage.getItem('session_context');
      const context = storedContext && storedContext !== 'undefined' ? JSON.parse(storedContext) : {};

      if (resource.resource_type === 'patent' || resource.resource_type === 'publication' || resource.resource_type === 'application') {
        const resourceIds = context.resource_ids || [];
        const patents = context.current_patents || [];
        const publications = context.current_publications || [];
        const applications = context.current_applications || [];

        // Check for duplicates in the specific array
        let isDuplicate = false;
        if (resource.resource_type === 'patent') {
          isDuplicate = patents.includes(resource.resource_id);
        } else if (resource.resource_type === 'publication') {
          isDuplicate = publications.includes(resource.resource_id);
        } else if (resource.resource_type === 'application') {
          isDuplicate = applications.includes(resource.resource_id);
        }

        if (isDuplicate) {
          addNotification({
            type: 'info',
            title: 'Already in Context',
            message: `${resource.resource_id} is already in your session context`,
          });
          return;
        }

        // Add to appropriate array based on type
        if (resource.resource_type === 'patent') {
          patents.push(resource.resource_id);
          context.current_patents = patents;
        } else if (resource.resource_type === 'publication') {
          publications.push(resource.resource_id);
          context.current_publications = publications;
        } else if (resource.resource_type === 'application') {
          applications.push(resource.resource_id);
          context.current_applications = applications;
        }

        localStorage.setItem('session_context', JSON.stringify(context));

        addNotification({
          type: 'success',
          title: 'Added to Context',
          message: `${resource.resource_id} added to your session`,
        });

        // Dispatch event to notify other components
        window.dispatchEvent(new Event('storage-update'));
      } else if (resource.resource_type === 'draft') {
        // Fetch the full draft details
        try {
          const draftDetails = await draftApi.getDraft(resource.resource_id);
          if (!draftDetails) {
            throw new Error('Draft not found');
          }

          // Add to active_drafts in localStorage
          const activeDraftsStr = localStorage.getItem('active_drafts');
          const activeDrafts = activeDraftsStr ? JSON.parse(activeDraftsStr) : [];

          // Check if already in active drafts
          if (activeDrafts.some((d: any) => d.draft_id === resource.resource_id)) {
            addNotification({
              type: 'info',
              title: 'Already Active',
              message: `${draftDetails.title || 'Draft'} is already in active drafts`,
            });
            return;
          }

          // Add the draft
          activeDrafts.push(draftDetails);
          localStorage.setItem('active_drafts', JSON.stringify(activeDrafts));

          // Also update session_context
          const sessionContext = storedContext && storedContext !== 'undefined' ? JSON.parse(storedContext) : {};
          sessionContext.current_drafts = activeDrafts;
          localStorage.setItem('session_context', JSON.stringify(sessionContext));

          addNotification({
            type: 'success',
            title: 'Draft Activated',
            message: `${draftDetails.title || 'Draft'} added to active drafts`,
          });

          // Dispatch event to notify other components
          window.dispatchEvent(new Event('storage-update'));
        } catch (error) {
          console.error('Error adding draft to context:', error);
          addNotification({
            type: 'error',
            title: 'Failed to Add Draft',
            message: 'Could not add draft to active drafts',
          });
        }
      } else {
        addNotification({
          type: 'error',
          title: 'Unsupported Resource',
          message: `Resource type "${resource.resource_type}" is not supported`,
        });
      }
    } catch (error) {
      console.error('Error adding resource to context:', error);
      addNotification({
        type: 'error',
        title: 'Error',
        message: 'Failed to add resource to context',
      });
    }
  }, [addNotification]);

  // Now process the data (this can use messageContext safely)
  if (!messageContext) {
    return null;
  }

  // Deduplicate resources, especially drafts (which might appear twice - once with name, once with just ID)
  const rawResources = messageContext.resources || [];
  const resourceMap = new Map<string, MessageContextResource>();

  rawResources.forEach(resource => {
    // Create a unique key based on type and ID
    const key = `${resource.resource_type || 'unknown'}_${resource.resource_id}`;

    // If this resource already exists, only replace if the new one has a better name
    const existing = resourceMap.get(key);
    if (existing) {
      // Prefer resources with resource_name over those without
      if (resource.resource_name && !existing.resource_name) {
        resourceMap.set(key, resource);
      }
      // Skip if existing is better or equal
    } else {
      resourceMap.set(key, resource);
    }
  });

  const resources = Array.from(resourceMap.values());
  const vdbResources = messageContext.vdb_discovered_resources || {};
  const searchTerms = messageContext.search_terms || [];
  // The context is stored as an object on some paths and a list of them on others, and
  // both reach the page as they were stored. Read from whichever it is rather than
  // assuming one, as the backend already does for the discovered resources.
  const figures: MessageFigure[] = Array.isArray(messageContext)
    ? ((messageContext as unknown as Array<{ figures?: MessageFigure[] }>)
        .flatMap(item => item?.figures ?? []))
    : (messageContext.figures ?? []);

  // Merge top-level sections with vdb_discovered_resources sections
  const mpepSections = [
    ...(messageContext.mpep_sections || []),
    ...(vdbResources.mpep_sections || [])
  ].filter((v, i, a) => a.indexOf(v) === i); // Remove duplicates

  const cfrSections = [
    ...(messageContext.cfr_sections || []),
    ...(vdbResources.cfr_sections || [])
  ].filter((v, i, a) => a.indexOf(v) === i); // Remove duplicates

  const uscSections = [
    ...(messageContext.usc_sections || []),
    ...(vdbResources.usc_sections || [])
  ].filter((v, i, a) => a.indexOf(v) === i); // Remove duplicates

  // Check if there's any content to display
  const hasResources = resources.length > 0;
  const hasVdbResources = mpepSections.length > 0 || cfrSections.length > 0 ||
                          uscSections.length > 0 || vdbResources.classification_codes?.length ||
                          vdbResources.fed_reg_docs?.length;
  const hasSearchTerms = searchTerms.length > 0;
  const hasFigures = figures.length > 0;
  const hasAnyContent = hasResources || hasVdbResources || hasSearchTerms || hasFigures;

  // If no content at all, show "no documents used" message
  if (!hasAnyContent) {
    return (
      <div className="rounded-lg">
        <div className="flex items-center gap-2">
          <FileText className="w-3.5 h-3.5 text-gray-400" />
          <span className="text-xs text-gray-600 italic">
            No specific documents were used in generating this response
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
      <p className="mb-1.5 text-xs font-medium text-blue-800">Sources used in this response</p>
      {hasFigures && (
        <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-blue-700 whitespace-nowrap">
            Drawings read:
          </span>
          {figures.map((figure, idx) => (
            <button
              key={`${figure.resource_id}-${figure.page}-${idx}`}
              type="button"
              onClick={() => setViewing(figure)}
              className="inline-flex items-center gap-1 rounded border border-blue-300 bg-white px-1.5 py-0.5 text-xs text-blue-800 hover:bg-blue-100"
              title={`Open the sheet this answer was given — page ${figure.page}${
                figure.label ? ` of ${figure.label}` : ''
              }`}
            >
              <ImageIcon className="h-3 w-3" />
              FIG. {figure.figure}
              <span className="text-blue-500">p. {figure.page}</span>
            </button>
          ))}
        </div>
      )}
      <div className={`flex ${expanded ? 'items-start' : 'items-center'} justify-between gap-2`}>
        <div className="flex-1 min-w-0">
          {expanded ? (
            <>
              {/* Expanded mode - full details */}
              {resources.length > 0 && (
                <div className="mb-2">
                  <div className="text-xs font-medium text-blue-700 mb-2">References Used:</div>
                  <div className="flex flex-wrap gap-2">
                    {resources.map((resource, idx) => (
                      <ResourceBadge
                        key={idx}
                        resource={resource}
                        expanded={expanded}
                        getResourceIcon={getResourceIcon}
                        getResourceDisplayName={getResourceDisplayName}
                        isResourceInContext={isResourceInContext}
                        handleAddToContext={handleAddToContext}
                        onDocumentClick={onDocumentClick}
                      />
                    ))}
                  </div>
                </div>
              )}
            </>
          ) : (
            <>
              {/* Collapsed mode - inline display */}
              <div className="flex items-center gap-1.5 flex-wrap" title="These resources were used in generating the message below.">
                {resources.length > 0 && (
                  <>
                    <span className="text-xs font-medium text-blue-700 whitespace-nowrap">References:</span>
                    {resources.slice(0, 4).map((resource, idx) => (
                      <ResourceBadge
                        key={idx}
                        resource={resource}
                        expanded={expanded}
                        getResourceIcon={getResourceIcon}
                        getResourceDisplayName={getResourceDisplayName}
                        isResourceInContext={isResourceInContext}
                        handleAddToContext={handleAddToContext}
                        onDocumentClick={onDocumentClick}
                      />
                    ))}
                    {resources.length > 4 && (
                      <span className="text-xs text-gray-600">
                        +{resources.length - 4} more
                      </span>
                    )}
                  </>
                )}

                {/* MPEP/CFR/USC Sections - show independently of regular resources */}
                {mpepSections.length > 0 && (
                  <>
                    <BookOpen className="w-3 h-3 text-blue-700 flex-shrink-0" />
                    <span className="text-xs font-medium text-blue-700 whitespace-nowrap">MPEP:</span>
                    {mpepSections.slice(0, 6).map((section, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center bg-white border border-gray-200 rounded px-2 py-0.5 text-xs cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                        onClick={() => onDocumentClick?.({ id: section, type: 'Mpep', title: `MPEP ${section}`, resource_id: section })}
                        title={`Click to view MPEP ${section}`}
                      >
                        {section}
                      </span>
                    ))}
                    {mpepSections.length > 6 && (
                      <span className="text-xs text-gray-600">
                        +{mpepSections.length - 6} more
                      </span>
                    )}
                  </>
                )}

                {cfrSections.length > 0 && (
                  <>
                    <Scale className="w-3 h-3 text-blue-700 flex-shrink-0" />
                    <span className="text-xs font-medium text-blue-700 whitespace-nowrap">CFR:</span>
                    {cfrSections.slice(0, 6).map((section, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center bg-white border border-gray-200 rounded px-2 py-0.5 text-xs cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                        onClick={() => onDocumentClick?.({ id: section, type: 'Cfr', title: `37 CFR ${section}`, resource_id: section })}
                        title={`Click to view 37 CFR ${section}`}
                      >
                        {section}
                      </span>
                    ))}
                    {cfrSections.length > 6 && (
                      <span className="text-xs text-gray-600">
                        +{cfrSections.length - 6} more
                      </span>
                    )}
                  </>
                )}

                {uscSections.length > 0 && (
                  <>
                    <Scale className="w-3 h-3 text-blue-700 flex-shrink-0" />
                    <span className="text-xs font-medium text-blue-700 whitespace-nowrap">USC:</span>
                    {uscSections.slice(0, 6).map((section, idx) => (
                      <span
                        key={idx}
                        className="inline-flex items-center bg-white border border-gray-200 rounded px-2 py-0.5 text-xs cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                        onClick={() => onDocumentClick?.({ id: section, type: 'Usc', title: `35 USC ${section}`, resource_id: section })}
                        title={`Click to view 35 USC ${section}`}
                      >
                        {section}
                      </span>
                    ))}
                    {uscSections.length > 6 && (
                      <span className="text-xs text-gray-600">
                        +{uscSections.length - 6} more
                      </span>
                    )}
                  </>
                )}
              </div>
            </>
          )}

          {/* VDB Resources - Only show when expanded */}
          {expanded && (
            <>
              {mpepSections.length > 0 && (
                <div className="mb-2">
                  <div className="text-xs font-medium text-blue-700 mb-2 flex items-center gap-1">
                    <BookOpen className="w-3 h-3" />
                    MPEP Sections:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {mpepSections.map((section, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="text-xs bg-white cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                        onClick={() => onDocumentClick?.({ id: section, type: 'Mpep', title: `MPEP ${section}`, resource_id: section })}
                        title={`Click to view MPEP ${section}`}
                      >
                        {section}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {cfrSections.length > 0 && (
                <div className="mb-2">
                  <div className="text-xs font-medium text-blue-700 mb-2 flex items-center gap-1">
                    <Scale className="w-3 h-3" />
                    CFR Sections:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {cfrSections.map((section, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="text-xs bg-white cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                        onClick={() => onDocumentClick?.({ id: section, type: 'Cfr', title: `37 CFR ${section}`, resource_id: section })}
                        title={`Click to view 37 CFR ${section}`}
                      >
                        {section}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {uscSections.length > 0 && (
                <div className="mb-2">
                  <div className="text-xs font-medium text-blue-700 mb-2 flex items-center gap-1">
                    <Scale className="w-3 h-3" />
                    USC Sections:
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {uscSections.map((section, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="text-xs bg-white cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors"
                        onClick={() => onDocumentClick?.({ id: section, type: 'Usc', title: `35 USC ${section}`, resource_id: section })}
                        title={`Click to view 35 USC ${section}`}
                      >
                        {section}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {vdbResources.classification_codes && vdbResources.classification_codes.length > 0 && (
                <div className="mb-2">
                  <div className="text-xs font-medium text-blue-700 mb-2">Classification Codes:</div>
                  <div className="flex flex-wrap gap-1">
                    {vdbResources.classification_codes.map((code, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs bg-white">
                        {code}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {searchTerms.length > 0 && (
                <div className="mb-2">
                  <div className="text-xs font-medium text-blue-700 mb-2">Search Terms:</div>
                  <div className="flex flex-wrap gap-1">
                    {searchTerms.map((term, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {term}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Help and Expand/Collapse Buttons */}
        <div className="flex items-center gap-1 flex-shrink-0 self-start">
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0"
            onClick={() => setShowHelpModal(true)}
            title="Learn about message context"
          >
            <HelpCircle className="w-4 h-4 text-blue-600" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="h-6 w-6 p-0"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? (
              <ChevronUp className="w-8 h-8 text-blue-600" />
            ) : (
              <ChevronDown className="w-8 h-8 text-blue-600" />
            )}
          </Button>
        </div>
      </div>

      {/* Help Modal */}
      <Dialog open={Boolean(viewing)} onOpenChange={open => !open && setViewing(null)}>
        <DialogContent className="max-w-5xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-sm">
              {viewing ? `FIG. ${viewing.figure} — page ${viewing.page}` : ''}
              {viewing?.label ? ` of ${viewing.label}` : ''}
            </DialogTitle>
          </DialogHeader>
          {viewing && (
            <div className="min-h-0 flex-1 overflow-auto bg-white">
              {/* The same image the model was given, not a fresh render: what is opened
                  here is what was actually read. */}
              <img
                src={figurePageUrl(viewing)}
                alt={`FIG. ${viewing.figure}, page ${viewing.page}`}
                className="mx-auto h-auto w-full max-w-full"
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showHelpModal} onOpenChange={setShowHelpModal}>
        <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>About Message Context</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 text-sm overflow-y-auto pr-2">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">What is Message Context?</h4>
              <p className="text-gray-700 leading-relaxed">
                Message context shows which documents, sections, and resources were referenced by the AI when generating the response below.
                This helps you understand the sources and basis for the information provided.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">How are Documents Used?</h4>
              <p className="text-gray-700 leading-relaxed mb-2">
                When you have documents in you session documents (patents, publications, applications, uploads, drafts, and resources like MPEP),
                the AI can reference specific parts of these documents to answer your questions:
              </p>
              <ul className="list-disc list-inside space-y-1 text-gray-700 ml-2">
                <li><strong>Specific Paragraphs:</strong> Individual sections or paragraphs from the specification</li>
                <li><strong>Claims:</strong> Specific claims from patent documents</li>
                <li><strong>Summary/Abstract:</strong> High-level overviews of the invention</li>
                <li><strong>Full Description:</strong> The entire detailed description section</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Reference Types</h4>
              <div className="space-y-2 text-gray-700">
                <p><strong>Patents/Publications/Applications:</strong> Patent documents from your session or discovered through search</p>
                <p><strong>Uploads:</strong> PDF or text files you've uploaded to the session</p>
                <p><strong>Drafts:</strong> Patent application drafts you're actively working on</p>
                <p><strong>MPEP Sections:</strong> Manual of Patent Examining Procedure sections referenced for guidance</p>
                <p><strong>CFR Sections:</strong> Code of Federal Regulations (Title 37) sections for USPTO rules</p>
                <p><strong>USC Sections:</strong> United States Code (Title 35) sections for patent law</p>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Clicking References</h4>
              <p className="text-gray-700 leading-relaxed">
                Click any reference badge to open that document in the document viewer, making it easy to review
                the exact sections that informed the AI's response.
              </p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const MessageContext = memo(MessageContextComponent, (prevProps, nextProps) => {
  return (
    prevProps.messageContext === nextProps.messageContext &&
    prevProps.sessionContext === nextProps.sessionContext &&
    prevProps.onDocumentClick === nextProps.onDocumentClick &&
    prevProps.compactMode === nextProps.compactMode
  );
});
